import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { finalize } from "rxjs/operators";
import {
  Modalidad,
  MonitoreoFiltro,
  PlanDia,
  Ubicacion,
} from "src/app/shared/model/asistencia/asistencia.model";
import { Msg } from "src/app/utilerias/mensajes/Msg";
import { AsistenciaService } from "../asistencia.service";

/** Un dia de la rejilla, con o sin plan declarado. */
interface DiaDelPlan {
  fecha: string;
  etiqueta: string;
  diaSemana: string;
  esPasado: boolean;
  plan: PlanDia | null;
}

/**
 * Mi plan: donde voy a trabajar los proximos dias.
 *
 * Es la excepcion, no la regla. La modalidad base y el patron semanal
 * cubren lo repetitivo; esto cubre "el martes me quedo en casa" sin que
 * nadie tenga que reasignar nada.
 *
 * EL PASADO NO SE DECLARA
 * Los dias anteriores a hoy se pintan pero no se pueden tocar. Dejarlos
 * editables convertiria el plan en una forma de borrar un retardo ya
 * calificado; el servidor tambien lo rechaza, y aqui se evita que el
 * usuario descubra la regla por un error.
 */
@Component({
  selector: "app-mi-plan",
  standalone: true,
  templateUrl: "./mi-plan.component.html",
  styleUrls: ["./mi-plan.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
  ],
})
export class MiPlanComponent implements OnInit {
  dias: DiaDelPlan[] = [];
  ubicaciones: Ubicacion[] = [];
  loading = false;

  desde: Date = new Date();
  /** Dos semanas es lo que cabe de un vistazo y lo que la gente planea. */
  diasAMostrar = 14;

  constructor(
    private asistenciaService: AsistenciaService,
    private msg: Msg,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarUbicaciones();
    queueMicrotask(() => {
      this.consultar();
      this.cdr.detectChanges();
    });
  }

  consultar(): void {
    const filtro = new MonitoreoFiltro();
    filtro.desde = this.aIsoFecha(this.desde);
    filtro.hasta = this.aIsoFecha(this.sumarDias(this.desde, this.diasAMostrar - 1));

    this.loading = true;
    this.cdr.detectChanges();

    this.asistenciaService
      .consultarPlan(filtro)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (respuesta) => this.armarRejilla(respuesta?.list ?? []),
        error: () => {
          this.armarRejilla([]);
          this.msg.errorCall("No se pudo consultar su plan.", null, null);
        },
      });
  }

  semanaAnterior(): void {
    this.desde = this.sumarDias(this.desde, -7);
    this.consultar();
  }

  semanaSiguiente(): void {
    this.desde = this.sumarDias(this.desde, 7);
    this.consultar();
  }

  /** Cambia la modalidad de un dia y la guarda de inmediato. */
  declarar(dia: DiaDelPlan, modalidad: Modalidad): void {
    if (dia.esPasado) {
      return;
    }
    const plan = new PlanDia();
    plan.fecha = dia.fecha;
    plan.modalidadAm = modalidad;
    plan.modalidadPm = modalidad;
    plan.idUbicacion = dia.plan?.idUbicacion ?? null;

    this.asistenciaService.declararPlan(plan).subscribe({
      next: (respuesta) => {
        // El servidor responde con el plan guardado; si algo lo rechazo,
        // devuelve el motivo en el comentario y sin fecha.
        if (!respuesta?.fecha) {
          this.msg.warningCall(respuesta?.comentario || "No se pudo guardar.", null, null);
          return;
        }
        dia.plan = respuesta;
        this.cdr.detectChanges();
      },
      error: () => this.msg.errorCall("No se pudo guardar el plan.", null, null),
    });
  }

  cambiarSitio(dia: DiaDelPlan, idUbicacion: number): void {
    if (dia.esPasado || !dia.plan) {
      return;
    }
    const plan = new PlanDia();
    plan.fecha = dia.fecha;
    plan.modalidadAm = dia.plan.modalidadAm;
    plan.modalidadPm = dia.plan.modalidadPm;
    plan.idUbicacion = idUbicacion;

    this.asistenciaService.declararPlan(plan).subscribe({
      next: (respuesta) => {
        if (respuesta?.fecha) {
          dia.plan = respuesta;
          this.cdr.detectChanges();
        }
      },
      error: () => this.msg.errorCall("No se pudo guardar el sitio.", null, null),
    });
  }

  /** Quita la excepcion: el dia vuelve al patron o a la modalidad base. */
  quitar(dia: DiaDelPlan): void {
    if (dia.esPasado || !dia.plan) {
      return;
    }
    const plan = new PlanDia();
    plan.fecha = dia.fecha;

    this.asistenciaService.borrarPlan(plan).subscribe({
      next: () => {
        dia.plan = null;
        this.cdr.detectChanges();
      },
      error: () => this.msg.errorCall("No se pudo quitar el plan.", null, null),
    });
  }

  nombreModalidad(modalidad: string): string {
    const nombres: Record<string, string> = {
      OFICINA: "Oficina",
      HOME_OFFICE: "Home office",
      MIXTO: "Mixto",
    };
    return modalidad ? nombres[modalidad] ?? modalidad : "";
  }

  /** Quien declaro: no es lo mismo elegirlo que que te lo indiquen. */
  etiquetaOrigen(plan: PlanDia): string {
    const origenes: Record<string, string> = {
      EMPLEADO: "Lo declaró usted",
      LIDER: "Lo indicó su líder",
      RH: "Lo indicó Recursos Humanos",
    };
    return origenes[plan?.origenPlan] ?? "";
  }

  esActiva(dia: DiaDelPlan, modalidad: Modalidad): boolean {
    return dia.plan?.modalidadAm === modalidad;
  }

  // ------------------------------------------------------------------

  private armarRejilla(planes: PlanDia[]): void {
    const porFecha = new Map<string, PlanDia>();
    planes.forEach((plan) => porFecha.set(`${plan.fecha}`, plan));

    const hoy = this.aIsoFecha(new Date());
    this.dias = [];
    for (let i = 0; i < this.diasAMostrar; i++) {
      const fecha = this.sumarDias(this.desde, i);
      const iso = this.aIsoFecha(fecha);
      this.dias.push({
        fecha: iso,
        etiqueta: `${fecha.getDate()}`,
        diaSemana: this.nombreDiaSemana(fecha.getDay()),
        esPasado: iso < hoy,
        plan: porFecha.get(iso) ?? null,
      });
    }
  }

  private cargarUbicaciones(): void {
    this.asistenciaService.consultarUbicaciones(null).subscribe({
      next: (respuesta) => {
        this.ubicaciones = respuesta?.list ?? [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.ubicaciones = [];
      },
    });
  }

  private sumarDias(fecha: Date, dias: number): Date {
    const nueva = new Date(fecha.getTime());
    nueva.setDate(nueva.getDate() + dias);
    return nueva;
  }

  /** Partes locales: toISOString() restaria horas y cambiaria el dia. */
  private aIsoFecha(fecha: Date): string {
    const mes = `${fecha.getMonth() + 1}`.padStart(2, "0");
    const dia = `${fecha.getDate()}`.padStart(2, "0");
    return `${fecha.getFullYear()}-${mes}-${dia}`;
  }

  private nombreDiaSemana(dia: number): string {
    return ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][dia] ?? "";
  }
}
